import type {
  Announcement,
  CustomAnswer,
  DiagramClarificationQuestion,
  Hint,
  HintFocus,
  Infographic,
  InfographicImage,
  LangCode,
  LiveDiagram,
  RepNote,
  Screenshot,
  SentimentEvent,
  SentimentSample,
  ServerWsMessage,
  TranscriptLine,
  UseCase,
  UserRole,
} from "@scoach/types";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export type LangMode = string; // "en" | any LangCode | "bi"

export interface LiveMeetingState {
  meetingId: string | null;
  connected: boolean;
  listening: boolean;
  muted: boolean;
  langMode: LangMode;
  latencyMs: number | null;
  startedAt: number | null;
  sttError: string | null;
  /** Soft, actionable audio warning: audio is flowing but no speech is being
   * recognized (mic off / "Share tab audio" not enabled). Amber, not a hard error. */
  audioWarning: string | null;
  /** Live speculative text from STT — overwrites itself as the speaker talks.
   * Cleared when the corresponding final transcript lands. */
  livePartial: string | null;
  /** Pending "the conversation switched to <to> from <from> — switch language?"
   * prompt (null when none / dismissed). Fed from the live/languageSwitch doc. */
  languageSwitchPrompt: { from: string; to: string } | null;

  transcript: TranscriptLine[];
  hints: Hint[];
  followups: string[];
  /** Time-ordered history of follow-up sets (newest last). Each set is the
   * full list returned by the server at that tick. Used by FollowupList to
   * show the latest set above and prior sets collapsed below. Cap at 8. */
  followupSets: { items: string[]; at: number }[];
  /** Combined sentiment series (legacy/global). */
  sentimentSeries: SentimentSample[];
  /** Sentiment series filtered to only what the rep ("You") said. */
  sentimentRep: SentimentSample[];
  /** Sentiment series filtered to only what the client said. */
  sentimentClient: SentimentSample[];
  sentimentEvents: SentimentEvent[];

  actedHintIds: Set<string>;
  pinnedHintIds: Set<string>;
  notes: RepNote[];
  liveTips: { id: string; text: string; at: number }[];
  /** Per-user custom Q&A answered live from the transcript on the user's Gemini key. */
  customSummary: CustomAnswer[];

  infographics: Infographic[];
  rightPanelTab:
    | "infographic"
    | "whatsnew"
    | "usecase"
    | "transcript"
    | "captions"
    | "diagram"
    | "decisiontree"
    | "plugins";
  /** Target language for live translated captions. null = captions off (default). */
  captionsLang: LangCode | null;
  quietInfographic: Infographic | null;
  activeInfographicIndex: number;

  infographicImages: InfographicImage[];
  activeImageIndex: number;
  screenshots: Screenshot[];
  announcements: Announcement[];
  /** Google Cloud customer stories surfaced live (relevant to the conversation). */
  useCases: UseCase[];
  /** Active keyword-tag filter for the hint list. null = show all. */
  activeTagFilter: string | null;
  /** Live architecture diagrams (technical meetings). */
  diagrams: LiveDiagram[];
  /** True when a diagram arrived that the user hasn't viewed yet (red tab dot). */
  unseenDiagrams: boolean;
  /** Pending clarification questions to ask before drawing a diagram (null = none). */
  diagramClarifications: DiagramClarificationQuestion[] | null;
  /** True while a diagram clarify/generate request is in flight (button spinner). */
  diagramGenerating: boolean;
  /** The custom-question preset key chosen for this meeting (role / meeting type). Drives
   * which question set the live "My questions" tab shows + answers. */
  meetingCustomQuestionKey: string | null;
  /** The role chosen for THIS meeting (changeable live) — biases hints, follow-ups,
   * infographic, and summary. */
  meetingRole: UserRole | null;
  pinnedChartIds: Set<string>;
  hintThreshold: number;
  /** Live "focus type" shaping hints (Technical/Executive/Deep dive/Project manager). */
  hintFocus: HintFocus;
  imageIntervalMin: number;
  infographicGenerating: boolean;

  // setters
  reset: () => void;
  setConnection: (connected: boolean) => void;
  setListening: (listening: boolean) => void;
  setMuted: (muted: boolean) => void;
  setLangMode: (m: LangMode) => void;
  setLatencyMs: (ms: number) => void;
  setMeetingId: (id: string) => void;
  setStartedAt: (ts: number) => void;
  setSttError: (err: string | null) => void;
  setAudioWarning: (w: string | null) => void;
  setLivePartial: (text: string | null) => void;
  setLanguageSwitchPrompt: (p: { from: string; to: string } | null) => void;
  applyServerMessage: (msg: ServerWsMessage) => void;
  markHintActed: (id: string) => void;
  togglePinned: (id: string) => void;
  addNote: (n: RepNote) => void;
  setNotes: (notes: RepNote[]) => void;
  updateNote: (index: number, text: string) => void;
  deleteNote: (index: number) => void;
  addInfographic: (ig: Infographic) => void;
  setInfographics: (igs: Infographic[]) => void;
  setRightPanelTab: (
    tab:
      | "infographic"
      | "whatsnew"
      | "usecase"
      | "transcript"
      | "captions"
      | "diagram"
      | "decisiontree"
      | "plugins",
  ) => void;
  setCaptionsLang: (lang: LangCode | null) => void;
  setQuietInfographic: (ig: Infographic | null) => void;
  setActiveInfographicIndex: (i: number) => void;
  addInfographicImage: (img: InfographicImage) => void;
  setInfographicImages: (imgs: InfographicImage[]) => void;
  setActiveImageIndex: (i: number) => void;
  togglePinnedChart: (id: string) => void;
  setHintThreshold: (n: number) => void;
  setHintFocus: (f: HintFocus) => void;
  setMeetingRole: (r: UserRole | null) => void;
  setImageIntervalMin: (n: number) => void;
  addScreenshot: (s: Screenshot) => void;
  addAnnouncement: (a: Announcement) => void;
  addUseCase: (u: UseCase) => void;
  setActiveTagFilter: (tag: string | null) => void;
  addDiagram: (d: LiveDiagram) => void;
  markDiagramsSeen: () => void;
  setDiagramClarifications: (qs: DiagramClarificationQuestion[] | null) => void;
  setDiagramGenerating: (v: boolean) => void;
  setMeetingCustomQuestionKey: (key: string | null) => void;
  setInfographicGenerating: (v: boolean) => void;
  setCustomSummary: (answers: CustomAnswer[]) => void;
}

export const useLiveMeetingStore = create<LiveMeetingState>()(
  subscribeWithSelector((set) => ({
    meetingId: null,
    connected: false,
    listening: false,
    muted: false,
    langMode: "bi",
    latencyMs: null,
    startedAt: null,
    sttError: null,
    audioWarning: null,
    livePartial: null,
    languageSwitchPrompt: null,
    transcript: [],
    hints: [],
    followups: [],
    followupSets: [],
    sentimentSeries: [],
    sentimentRep: [],
    sentimentClient: [],
    sentimentEvents: [],
    actedHintIds: new Set<string>(),
    pinnedHintIds: new Set<string>(),
    notes: [],
    liveTips: [],
    customSummary: [],
    infographics: [],
    rightPanelTab: "infographic",
    captionsLang: null,
    quietInfographic: null,
    activeInfographicIndex: 0,
    infographicImages: [],
    activeImageIndex: 0,
    screenshots: [],
    announcements: [],
    useCases: [],
    activeTagFilter: null,
    diagrams: [],
    unseenDiagrams: false,
    diagramClarifications: null,
    diagramGenerating: false,
    meetingCustomQuestionKey: null,
    meetingRole: null,
    pinnedChartIds: new Set<string>(),
    hintThreshold: 0.85,
    hintFocus: "standard",
    imageIntervalMin: 5,
    infographicGenerating: false,

    reset: () =>
      set({
        meetingId: null,
        connected: false,
        listening: false,
        muted: false,
        latencyMs: null,
        startedAt: null,
        sttError: null,
        audioWarning: null,
        livePartial: null,
        languageSwitchPrompt: null,
        transcript: [],
        hints: [],
        followups: [],
        followupSets: [],
        sentimentSeries: [],
        sentimentRep: [],
        sentimentClient: [],
        sentimentEvents: [],
        actedHintIds: new Set<string>(),
        pinnedHintIds: new Set<string>(),
        notes: [],
        liveTips: [],
        customSummary: [],
        infographics: [],
        rightPanelTab: "infographic",
        quietInfographic: null,
        activeInfographicIndex: 0,
        infographicImages: [],
        activeImageIndex: 0,
        screenshots: [],
        announcements: [],
        useCases: [],
        activeTagFilter: null,
        diagrams: [],
        unseenDiagrams: false,
        diagramClarifications: null,
        diagramGenerating: false,
        meetingCustomQuestionKey: null,
        meetingRole: null,
        pinnedChartIds: new Set<string>(),
        hintThreshold: 0.85,
        hintFocus: "standard",
        imageIntervalMin: 5,
        infographicGenerating: false,
      }),
    setConnection: (connected) => set({ connected }),
    setListening: (listening) => set({ listening }),
    setMuted: (muted) => set({ muted }),
    setLangMode: (langMode) => set({ langMode }),
    setLatencyMs: (latencyMs) => set({ latencyMs }),
    setMeetingId: (meetingId) => set({ meetingId }),
    setStartedAt: (startedAt) => set({ startedAt }),
    setSttError: (sttError) => set({ sttError }),
    setAudioWarning: (audioWarning) => set({ audioWarning }),
    setLivePartial: (livePartial) => set({ livePartial }),
    setLanguageSwitchPrompt: (languageSwitchPrompt) => set({ languageSwitchPrompt }),

    applyServerMessage: (msg) =>
      set((s) => {
        switch (msg.type) {
          case "transcript-final":
          case "transcript-partial": {
            const idx = s.transcript.findIndex((l) => l.id === msg.line.id);
            if (idx >= 0) {
              const next = [...s.transcript];
              next[idx] = msg.line;
              return { transcript: next };
            }
            return { transcript: [...s.transcript, msg.line] };
          }
          case "hint": {
            if (s.hints.some((h) => h.id === msg.hint.id)) return {};
            return { hints: [msg.hint, ...s.hints] };
          }
          case "followups": {
            const last = s.followupSets[s.followupSets.length - 1];
            // Skip if items haven't changed — keeps history meaningful.
            if (last && JSON.stringify(last.items) === JSON.stringify(msg.items)) {
              return { followups: msg.items };
            }
            return {
              followups: msg.items,
              followupSets: [...s.followupSets, { items: msg.items, at: Date.now() }].slice(-8),
            };
          }
          case "sentiment": {
            const speaker = msg.sample.speaker ?? "all";
            const events = msg.sample.event
              ? [...s.sentimentEvents, msg.sample.event]
              : s.sentimentEvents;
            if (speaker === "rep") {
              return { sentimentRep: [...s.sentimentRep, msg.sample], sentimentEvents: events };
            }
            if (speaker === "client") {
              return {
                sentimentClient: [...s.sentimentClient, msg.sample],
                sentimentEvents: events,
              };
            }
            return { sentimentSeries: [...s.sentimentSeries, msg.sample], sentimentEvents: events };
          }
          case "tip": {
            if (s.liveTips.some((t) => t.id === msg.tip.id)) return {};
            return { liveTips: [msg.tip, ...s.liveTips] };
          }
          case "pong":
            return { latencyMs: msg.latencyMs };
          default:
            return {};
        }
      }),

    markHintActed: (id) =>
      set((s) => {
        const next = new Set(s.actedHintIds);
        next.add(id);
        return { actedHintIds: next };
      }),
    // Only ONE hint can be pinned at a time: pinning a hint clears any other pin;
    // pinning the already-pinned hint unpins it.
    togglePinned: (id) =>
      set((s) => ({
        pinnedHintIds: s.pinnedHintIds.has(id) ? new Set<string>() : new Set<string>([id]),
      })),
    addNote: (n) => set((s) => ({ notes: [...s.notes, n] })),
    setNotes: (notes) => set({ notes }),
    updateNote: (index, text) =>
      set((s) => {
        if (index < 0 || index >= s.notes.length) return {};
        const next = [...s.notes];
        const existing = next[index];
        if (!existing) return {};
        next[index] = { t: existing.t, text };
        return { notes: next };
      }),
    deleteNote: (index) =>
      set((s) => {
        if (index < 0 || index >= s.notes.length) return {};
        const next = s.notes.filter((_, i) => i !== index);
        return { notes: next };
      }),
    addInfographic: (ig) =>
      set((s) => {
        if (s.infographics.some((x) => x.id === ig.id)) return {};
        return { infographics: [ig, ...s.infographics].slice(0, 20), activeInfographicIndex: 0 };
      }),
    setInfographics: (infographics) => set({ infographics, activeInfographicIndex: 0 }),
    setRightPanelTab: (rightPanelTab) =>
      set(
        rightPanelTab === "diagram" || rightPanelTab === "decisiontree"
          ? { rightPanelTab, unseenDiagrams: false }
          : { rightPanelTab },
      ),
    setCaptionsLang: (captionsLang) => set({ captionsLang }),
    setQuietInfographic: (quietInfographic) => set({ quietInfographic }),
    setActiveInfographicIndex: (activeInfographicIndex) => set({ activeInfographicIndex }),
    addInfographicImage: (img) =>
      set((s) => {
        if (s.infographicImages.some((x) => x.id === img.id)) return {};
        return {
          infographicImages: [img, ...s.infographicImages].slice(0, 20),
          activeImageIndex: 0,
        };
      }),
    setInfographicImages: (infographicImages) => set({ infographicImages, activeImageIndex: 0 }),
    setActiveImageIndex: (activeImageIndex) => set({ activeImageIndex }),
    togglePinnedChart: (id) =>
      set((s) => {
        const next = new Set(s.pinnedChartIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return { pinnedChartIds: next };
      }),
    setHintThreshold: (hintThreshold) => set({ hintThreshold }),
    setHintFocus: (hintFocus) => set({ hintFocus }),
    setImageIntervalMin: (imageIntervalMin) => set({ imageIntervalMin }),
    addScreenshot: (s) =>
      set((st) => {
        if (st.screenshots.some((x) => x.id === s.id)) return {};
        return { screenshots: [...st.screenshots, s] };
      }),
    addAnnouncement: (a) =>
      set((st) => {
        if (st.announcements.some((x) => x.id === a.id)) return {};
        return { announcements: [a, ...st.announcements].slice(0, 20) };
      }),
    addUseCase: (u) =>
      set((st) => {
        if (st.useCases.some((x) => x.id === u.id)) return {};
        // Highest-rated first, then newest, so the most suitable stories lead.
        const useCases = [u, ...st.useCases]
          .sort((a, b) => b.rating - a.rating || b.generatedAt.localeCompare(a.generatedAt))
          .slice(0, 20);
        return { useCases };
      }),
    setActiveTagFilter: (activeTagFilter) => set({ activeTagFilter }),
    addDiagram: (d) =>
      set((st) => {
        if (st.diagrams.some((x) => x.id === d.id)) return {};
        // Newest first; flag unseen unless the user is already on a diagram tab.
        const diagrams = [d, ...st.diagrams].slice(0, 20);
        const onDiagramTab = st.rightPanelTab === "diagram" || st.rightPanelTab === "decisiontree";
        return { diagrams, unseenDiagrams: !onDiagramTab };
      }),
    markDiagramsSeen: () => set({ unseenDiagrams: false }),
    setDiagramClarifications: (diagramClarifications) =>
      set((st) => ({
        diagramClarifications,
        // A fresh question set the user hasn't seen also lights the tab dot.
        unseenDiagrams:
          diagramClarifications && diagramClarifications.length > 0
            ? st.rightPanelTab !== "diagram"
            : st.unseenDiagrams,
      })),
    setDiagramGenerating: (diagramGenerating) => set({ diagramGenerating }),
    setMeetingCustomQuestionKey: (meetingCustomQuestionKey) => set({ meetingCustomQuestionKey }),
    setMeetingRole: (meetingRole) => set({ meetingRole }),
    setInfographicGenerating: (infographicGenerating) => set({ infographicGenerating }),
    setCustomSummary: (customSummary) => set({ customSummary }),
  })),
);
